'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { ChatGptMark, ClaudeMark } from '@/app/ai-assistant/workspace/AssistantConnectorsDialog';
import { trackEvent } from '@/app/lib/posthog';
import styles from './LandingHome.module.css';
import { formatClock, prefersReducedMotion, useSnapshots, type Meeting } from './landingData';

const COLLEGE_AVE_TO_LIVINGSTON_MINUTES = 25; // TRAVEL_TIMES in app/lib/constants.ts

function useTyped(text: string, run: boolean, perTick = 2, tickMs = 36) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!run) return;
    if (prefersReducedMotion()) { setShown(text.length); return; }
    const id = window.setInterval(() => setShown((n) => {
      if (n >= text.length) { window.clearInterval(id); return n; }
      return Math.min(text.length, n + perTick);
    }), tickMs);
    return () => window.clearInterval(id);
  }, [run, text, perTick, tickMs]);
  return { text: text.slice(0, shown), done: shown >= text.length };
}

/** "Ask in your own AI": connector actions plus a conversation that types itself out on the snapshot schedule's tightest College Ave → Livingston hop. */
export default function AskSection() {
  const data = useSnapshots('schedule', 'bus');
  const chatRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState(0); // 0 idle, 1 question, 2 tool, 3 tool done, 4 answer

  // The tightest College Ave → Livingston hop on the snapshot schedule (same day, ≥ the planned travel time apart).
  const hop = (() => {
    const all = (data?.schedule.schedule ?? []).flatMap((c) => c.meetings);
    let best: { from: Meeting; to: Meeting } | null = null;
    for (const f of all) for (const t of all) {
      if (f.day !== t.day || !f.campus.startsWith('College') || t.campus !== 'Livingston' || t.start < f.end) continue;
      if (!best || t.start - f.end < best.to.start - best.from.end) best = { from: f, to: t };
    }
    return best;
  })();
  const from = hop?.from;
  const to = hop?.to;
  const dayName = from ? ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'][from.day] : '';
  const lx = data?.bus.routes.find((r) => r.short === 'LX');
  const question = from && to ? `I'm in ${from.where} on College Ave until ${formatClock(from.end)} on ${dayName}. Will I make ${to.where} on Livingston by ${formatClock(to.start)}?` : '';
  const answer = from && to ? `Yes. You have ${to.start - from.end} minutes, and Scarlet Sync plans about ${COLLEGE_AVE_TO_LIVINGSTON_MINUTES} for College Ave to Livingston. Take the LX from College Avenue Student Center.` : '';

  const q = useTyped(question, phase >= 1);
  const a = useTyped(answer, phase >= 4, 2, 28);

  useEffect(() => {
    const el = chatRef.current;
    if (!el || !question) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setPhase((p) => Math.max(p, 1)); io.disconnect(); }
    }, { threshold: 0.45 });
    io.observe(el);
    return () => io.disconnect();
  }, [question]);

  useEffect(() => {
    const quick = prefersReducedMotion();
    if (phase === 1 && q.done) { const t = window.setTimeout(() => setPhase(2), quick ? 0 : 350); return () => window.clearTimeout(t); }
    if (phase === 2) { const t = window.setTimeout(() => setPhase(3), quick ? 0 : 1300); return () => window.clearTimeout(t); }
    if (phase === 3) { const t = window.setTimeout(() => setPhase(4), quick ? 0 : 250); return () => window.clearTimeout(t); }
  }, [phase, q.done]);

  const caret = <span className={styles.caret} aria-hidden="true" />;

  return (
    <section className={styles.ask} aria-labelledby="ask-own-ai">
      <div className={styles.askInner}>
        <div>
          <h2 id="ask-own-ai" className={styles.askTitle}>Ask in <span className={styles.gradientSwipe}>your own AI</span>.</h2>
          <p className={styles.askQuestion}>&ldquo;Will I make it from College Ave to Livingston?&rdquo;</p>
          <div className={styles.ctas}>
            <Link className={styles.btnGhost} href="/support/developer-connectors" onClick={() => trackEvent('landing_cta_click', { cta: 'add_chatgpt', location: 'ask_section' })}><ChatGptMark />Add to ChatGPT</Link>
            <Link className={styles.btnGhost} href="/support/developer-connectors" onClick={() => trackEvent('landing_cta_click', { cta: 'add_claude', location: 'ask_section' })}><ClaudeMark />Add to Claude</Link>
            <Link className={styles.btnPrimary} href="/ai-assistant" onClick={() => trackEvent('landing_cta_click', { cta: 'open_assistant', location: 'ask_section' })}>
              {/* eslint-disable-next-line @next/next/no-img-element -- 20px brand mark */}
              <img className={styles.btnLogo} src="/logo/scarlet-sync-no-bg.png" alt="" />Open Scarlet Sync assistant
            </Link>
          </div>
        </div>
        <div ref={chatRef} className={styles.chat} aria-label="Example conversation with Ask Scarlet Sync">
          <div className={styles.chatHead}>
            {/* eslint-disable-next-line @next/next/no-img-element -- 24px brand mark */}
            <img src="/logo/scarlet-sync-no-bg.png" alt="" />Ask Scarlet Sync
          </div>
          <div className={styles.chatBody}>
            <div className={`${styles.me} ${phase >= 1 ? styles.bubbleOn : ''}`}>{q.text}{phase >= 1 && !q.done ? caret : null}</div>
            <div className={`${styles.tool} ${phase >= 2 ? styles.toolOn : ''}`}>
              <span className={phase >= 3 ? styles.spinDone : styles.spin} />
              <span>{phase >= 3 ? `Checked LX route · your ${dayName}` : 'Checking bus routes and your schedule'}</span>
            </div>
            <div className={`${styles.ai} ${phase >= 4 ? styles.bubbleOn : ''}`}>
              {a.text}{phase >= 4 && !a.done ? caret : null}
              {from && to && lx ? (
                <div className={`${styles.artifact} ${a.done && phase >= 4 ? styles.artifactOn : ''}`}>
                  <div className={styles.leg}><b>{from.where}</b>{formatClock(from.end)}</div>
                  <span className={styles.pill} style={{ ['--c' as string]: lx.color }}>LX</span>
                  <div className={styles.leg}><b>{to.where}</b>{formatClock(to.start)}</div>
                </div>
              ) : null}
            </div>
          </div>
          <div className={styles.chatInput}>Ask about classes, buses, dining…</div>
        </div>
      </div>
    </section>
  );
}
